// D7-06 (FE-6.5): shared copy-to-clipboard hook. The "copy + show a transient
// 'Copied' state" pattern was duplicated (with slightly different timeouts and
// missing timeout cleanup) across MessageBubble, MarkdownRenderer, ChatHeader
// (share dialog + copy-link action) and SharedConversationPage. One hook, one
// behavior, one place to fix bugs.
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCopyToClipboardResult {
  /** True for `resetAfterMs` after a successful copy. */
  copied: boolean;
  /** Copy `text` to the clipboard. Resolves `true` on success. */
  copy: (text: string) => Promise<boolean>;
  /** Clear the copied state immediately (e.g. when a dialog closes). */
  reset: () => void;
}

export function useCopyToClipboard(resetAfterMs = 1500): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const clearResetTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Never let a pending timeout fire against an unmounted component.
  useEffect(() => clearResetTimeout, [clearResetTimeout]);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      clearResetTimeout();
      setCopied(true);
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        setCopied(false);
      }, resetAfterMs);
      return true;
    } catch {
      clearResetTimeout();
      setCopied(false);
      return false;
    }
  }, [clearResetTimeout, resetAfterMs]);

  const reset = useCallback(() => {
    clearResetTimeout();
    setCopied(false);
  }, [clearResetTimeout]);

  return { copied, copy, reset };
}

export default useCopyToClipboard;
